(() => {
  if (customElements.get('live-viewer-count')) return;

  class LiveViewerCount extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.countNode = this.querySelector('[data-live-viewer-value]');
      this.messageNode = this.querySelector('[data-live-viewer-message]');
      this.statusNode = this.querySelector('[data-live-viewer-status]');
      this.productKey = this.dataset.productKey || 'default';
      this.currentCount = this.number('initialCount', this.number('minimum', 6));
      this.minimum = this.number('minimum', 6);
      this.maximum = Math.max(this.minimum, this.number('maximum', 28));
      this.variation = Math.max(1, this.number('variation', 3));
      this.pollInterval = Math.max(3000, this.number('pollInterval', 12000));
      this.sourceMode = this.dataset.sourceMode || 'simulation';
      this.mount();
      this.watchVariant();
      this.refresh(true);
      this.timer = window.setInterval(() => this.refresh(false), this.pollInterval);
    }

    disconnectedCallback() {
      window.clearInterval(this.timer);
      document.removeEventListener('change', this.onVariantChange);
    }

    number(name, fallback) {
      const value = Number(this.dataset[name]);
      return Number.isFinite(value) ? value : fallback;
    }

    mount() {
      const selector = this.dataset.mountSelector?.trim();
      if (!selector) return;
      let target;
      try {
        target = document.querySelector(selector);
      } catch (error) {
        console.warn('Live viewer count: invalid mount selector.', error);
        return;
      }
      if (!target || target === this || this.contains(target)) return;

      const position = this.dataset.mountPosition || 'afterend';
      if (position === 'inside-start') target.prepend(this);
      else if (position === 'inside-end') target.append(this);
      else target.insertAdjacentElement(position, this);
    }

    watchVariant() {
      const selector = this.dataset.variantSelector?.trim();
      if (!selector) return;
      this.onVariantChange = (event) => {
        let input;
        try {
          input = event.target?.matches?.(selector) ? event.target : null;
        } catch (error) {
          console.warn('Live viewer count: invalid variant selector.', error);
          document.removeEventListener('change', this.onVariantChange);
          return;
        }
        if (!input?.value) return;
        this.dataset.variantId = input.value;
        this.refresh(true);
      };
      document.addEventListener('change', this.onVariantChange);
    }

    async refresh(immediate) {
      let nextCount = null;
      if (this.sourceMode === 'api' || this.sourceMode === 'api_with_fallback') {
        nextCount = await this.fetchCount();
      }
      if (nextCount === null && this.sourceMode !== 'api') nextCount = this.simulateCount();
      if (nextCount === null) return this.setUnavailable();
      this.render(this.clamp(Math.round(nextCount)), immediate);
    }

    async fetchCount() {
      const endpoint = this.buildEndpoint();
      if (!endpoint) return null;
      try {
        const response = await fetch(endpoint, {
          headers: { Accept: 'application/json' },
          credentials: this.dataset.sendCredentials === 'true' ? 'same-origin' : 'omit'
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const payload = await response.json();
        const value = this.readPath(payload, this.dataset.responsePath || 'count');
        return Number.isFinite(Number(value)) ? Number(value) : null;
      } catch (error) {
        console.warn('Live viewer count: data request failed.', error);
        return null;
      }
    }

    buildEndpoint() {
      return (this.dataset.endpoint || '')
        .replaceAll('[product_key]', encodeURIComponent(this.productKey))
        .replaceAll('[variant_id]', encodeURIComponent(this.dataset.variantId || ''));
    }

    readPath(payload, path) {
      return path.split('.').filter(Boolean).reduce((value, key) => value?.[key], payload);
    }

    simulateCount() {
      const dayBucket = Math.floor(Date.now() / 86400000);
      const seed = this.hash(`${this.productKey}:${dayBucket}`);
      const baseline = this.minimum + (seed % (this.maximum - this.minimum + 1));
      const previous = Number.isFinite(this.currentCount) ? this.currentCount : baseline;
      const direction = Math.random() < 0.5 ? -1 : 1;
      const step = 1 + Math.floor(Math.random() * this.variation);
      return this.clamp(Math.round(previous * 0.72 + baseline * 0.28 + direction * step));
    }

    hash(value) {
      let result = 2166136261;
      for (let index = 0; index < value.length; index += 1) {
        result ^= value.charCodeAt(index);
        result = Math.imul(result, 16777619);
      }
      return Math.abs(result);
    }

    clamp(value) {
      return Math.min(this.maximum, Math.max(this.minimum, value));
    }

    render(count, immediate) {
      this.currentCount = count;
      this.hidden = false;
      this.removeAttribute('aria-hidden');
      if (!this.countNode || !this.messageNode) return;

      const message = (this.dataset.messageTemplate || '[count]').replaceAll('[count]', String(count));
      const announce = (this.dataset.announceTemplate || this.dataset.messageTemplate || '[count]').replaceAll('[count]', String(count));
      this.countNode.textContent = String(count);
      this.messageNode.setAttribute('aria-label', message);
      if (this.statusNode) this.statusNode.textContent = announce;

      if (!immediate && this.dataset.animate !== 'false') {
        this.classList.remove('is-updating');
        void this.offsetWidth;
        this.classList.add('is-updating');
      }
    }

    setUnavailable() {
      if (this.dataset.hideWhenUnavailable === 'true') {
        this.hidden = true;
        this.setAttribute('aria-hidden', 'true');
        return;
      }
      if (this.statusNode) this.statusNode.textContent = this.dataset.unavailableText || '';
    }
  }

  customElements.define('live-viewer-count', LiveViewerCount);
})();

